import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Table,
  Loader,
  Segment,
  Header,
  Progress,
  Label,
  Grid,
  Statistic,
  Breadcrumb,
  Icon,
  Button,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

const PLATFORMS = ['nick', 'volto', 'volto_slate', 'volto_vlt'];

const statusColors = {
  fixed_local: '#21ba45',
  reported: '#2185d0',
  wontfix: '#767676',
  triaged: '#f2711c',
  open: '#db2828',
  acknowledged: '#6435c9',
  fixed_upstream: '#00b5ad',
};

const statusLabels = {
  fixed_local: 'Fixed',
  reported: 'Upstream',
  wontfix: "Won't Fix",
  triaged: 'Pending',
  open: 'Open',
  acknowledged: 'Acknowledged',
  fixed_upstream: 'Fixed Upstream',
};

const riskColors = {
  S4: '#db2828',
  S3: '#f2711c',
  S2: '#fbbd08',
  S1: '#b5b5b5',
  null: '#e8e8e8',
};

const QCDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchApi(`${getApiPath()}/@polyglot/qc-findings?mode=summary`)
      .then((json) => {
        setData(json.summary);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Loader active inline="centered" content="Loading QC data..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  const {
    by_platform_status, by_platform_risk, by_platform_error, totals,
    disposition_breakdown, recent_activity,
  } = data;

  // Group by_platform_status into { nick: { triaged: 440, ... }, ... }
  const platformStatus = {};
  by_platform_status.forEach((row) => {
    if (!platformStatus[row.platform]) platformStatus[row.platform] = {};
    platformStatus[row.platform][row.status] = row.count;
  });

  // Group by_platform_risk into { nick: { S1: 251, ... }, ... }
  const platformRisk = {};
  by_platform_risk.forEach((row) => {
    if (!platformRisk[row.platform]) platformRisk[row.platform] = {};
    platformRisk[row.platform][row.severity_code || 'unclassified'] =
      row.count;
  });

  // Group by_platform_error into { nick: [{ error_type, count }, ...], ... }
  const platformError = {};
  by_platform_error.forEach((row) => {
    if (!platformError[row.platform]) platformError[row.platform] = [];
    platformError[row.platform].push(row);
  });

  const dispositioned =
    totals.total - totals.triaged - totals.open;
  const pctComplete = totals.total > 0
    ? Math.round((dispositioned / totals.total) * 100)
    : 0;

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '2em' }}>
      <Helmet title="QC Findings Dashboard" />

      <Breadcrumb style={{ marginBottom: '1em' }}>
        <Breadcrumb.Section as={Link} to="/polyglot">Polyglot</Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>QC Dashboard</Breadcrumb.Section>
      </Breadcrumb>

      <div className="polyglot-page-header">
        <h1>QC Findings Dashboard</h1>
        <p className="subtitle">
          {totals.total.toLocaleString()} findings across{' '}
          {PLATFORMS.length} platforms
        </p>
      </div>

      {/* Key metrics */}
      <Segment>
        <Grid columns={5} stackable textAlign="center">
          <Grid.Column>
            <Statistic size="small">
              <Statistic.Value>{totals.total.toLocaleString()}</Statistic.Value>
              <Statistic.Label>Total</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic size="small" color="green">
              <Statistic.Value>
                {totals.fixed_local.toLocaleString()}
              </Statistic.Value>
              <Statistic.Label>Fixed</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic size="small" color="blue">
              <Statistic.Value>
                {totals.reported.toLocaleString()}
              </Statistic.Value>
              <Statistic.Label>Upstream</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic size="small" color="orange">
              <Statistic.Value>
                {totals.triaged.toLocaleString()}
              </Statistic.Value>
              <Statistic.Label>Pending</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic size="small">
              <Statistic.Value>{pctComplete}%</Statistic.Value>
              <Statistic.Label>Complete</Statistic.Label>
            </Statistic>
          </Grid.Column>
        </Grid>
        <Progress
          percent={pctComplete}
          color={pctComplete === 100 ? 'green' : pctComplete > 50 ? 'olive' : 'orange'}
          size="small"
          style={{ marginTop: '1em', marginBottom: 0 }}
        />
      </Segment>

      {/* Per-platform status */}
      <Header as="h2">Platform Status</Header>
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Platform</Table.HeaderCell>
            <Table.HeaderCell>Total</Table.HeaderCell>
            <Table.HeaderCell>Fixed</Table.HeaderCell>
            <Table.HeaderCell>Upstream</Table.HeaderCell>
            <Table.HeaderCell>Won't Fix</Table.HeaderCell>
            <Table.HeaderCell>Pending</Table.HeaderCell>
            <Table.HeaderCell>Progress</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {PLATFORMS.map((p) => {
            const s = platformStatus[p] || {};
            const total = Object.values(s).reduce((a, b) => a + b, 0);
            const done = total - (s.triaged || 0) - (s.open || 0);
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <Table.Row key={p}>
                <Table.Cell>
                  <Link to={`/polyglot/qc/findings?platform=${p}`}>
                    <strong>{p}</strong>
                  </Link>
                </Table.Cell>
                <Table.Cell>{total}</Table.Cell>
                <Table.Cell style={{ color: statusColors.fixed_local }}>
                  {s.fixed_local || 0}
                </Table.Cell>
                <Table.Cell style={{ color: statusColors.reported }}>
                  {s.reported || 0}
                </Table.Cell>
                <Table.Cell>{s.wontfix || 0}</Table.Cell>
                <Table.Cell style={{ color: statusColors.triaged }}>
                  {s.triaged || 0}
                </Table.Cell>
                <Table.Cell>
                  <Progress
                    percent={pct}
                    size="tiny"
                    color={pct === 100 ? 'green' : 'orange'}
                    style={{ marginBottom: 0 }}
                  >
                    {pct}%
                  </Progress>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      {/* Risk heatmap */}
      <Header as="h2">Risk Heatmap (Pending Findings)</Header>
      <Table celled compact definition>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell />
            <Table.HeaderCell>S4 (Critical)</Table.HeaderCell>
            <Table.HeaderCell>S3 (Serious)</Table.HeaderCell>
            <Table.HeaderCell>S2 (Minor)</Table.HeaderCell>
            <Table.HeaderCell>S1 (Cosmetic)</Table.HeaderCell>
            <Table.HeaderCell>Unclassified</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {PLATFORMS.map((p) => {
            const r = platformRisk[p] || {};
            return (
              <Table.Row key={p}>
                <Table.Cell><strong>{p}</strong></Table.Cell>
                {['S4', 'S3', 'S2', 'S1', 'unclassified'].map((sev) => {
                  const count = r[sev] || 0;
                  return (
                    <Table.Cell
                      key={sev}
                      style={{
                        backgroundColor: count > 0 ? riskColors[sev === 'unclassified' ? 'null' : sev] : undefined,
                        color: (sev === 'S4' || sev === 'S3') && count > 0 ? '#fff' : undefined,
                        textAlign: 'center',
                        fontWeight: count > 0 ? 'bold' : 'normal',
                      }}
                    >
                      {count > 0 ? (
                        <Link
                          to={`/polyglot/qc/findings?platform=${p}&status=triaged${sev !== 'unclassified' ? `&risk=${sev}` : ''}`}
                          style={{ color: 'inherit' }}
                        >
                          {count}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      {/* Error type breakdown */}
      <Header as="h2">Error Types (Pending Findings)</Header>
      <Grid columns={2} stackable>
        {PLATFORMS.filter((p) => platformError[p] && platformError[p].length > 0).map(
          (p) => (
            <Grid.Column key={p}>
              <Segment>
                <Header as="h3">{p}</Header>
                <Table compact size="small">
                  <Table.Body>
                    {(platformError[p] || []).slice(0, 8).map((row) => (
                      <Table.Row key={row.error_type}>
                        <Table.Cell>
                          <Link
                            to={`/polyglot/qc/findings?platform=${p}&status=triaged&error_type=${row.error_type}`}
                          >
                            {row.error_type_label || row.error_type}
                          </Link>
                        </Table.Cell>
                        <Table.Cell textAlign="right">{row.count}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Segment>
            </Grid.Column>
          ),
        )}
      </Grid>

      {/* Disposition Breakdown (auditor evidence) */}
      {disposition_breakdown && disposition_breakdown.length > 0 && (
        <>
          <Header as="h2">Disposition Breakdown</Header>
          <Grid columns={2} stackable>
            <Grid.Column>
              <Table compact celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Disposition</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Count</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {disposition_breakdown.map((row) => {
                    const labels = {
                      acceptable: { text: 'Acceptable', color: 'green', icon: 'check' },
                      confirmed: { text: 'Issue Confirmed', color: 'orange', icon: 'exclamation triangle' },
                      false_positive: { text: 'False Positive', color: 'grey', icon: 'ban' },
                      none: { text: 'Not yet reviewed', color: null, icon: 'clock outline' },
                    };
                    const cfg = labels[row.disposition] || { text: row.disposition, color: null, icon: 'question' };
                    return (
                      <Table.Row key={row.disposition}>
                        <Table.Cell>
                          <Icon name={cfg.icon} color={cfg.color || undefined} />
                          {cfg.text}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          <strong>{row.count.toLocaleString()}</strong>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </Grid.Column>
            <Grid.Column>
              <Segment>
                <Header as="h4">
                  <Icon name="eye" />
                  Audit Views
                </Header>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6em' }}>
                  <Button
                    as={Link}
                    to="/polyglot/qc/findings?status=fixed_local"
                    basic
                    color="green"
                    fluid
                    size="small"
                  >
                    <Icon name="check circle" />
                    View Fixed Findings ({totals.fixed_local.toLocaleString()})
                  </Button>
                  <Button
                    as={Link}
                    to="/polyglot/qc/findings?status=wontfix"
                    basic
                    color="grey"
                    fluid
                    size="small"
                  >
                    <Icon name="ban" />
                    View Dismissed Findings ({totals.wontfix.toLocaleString()})
                  </Button>
                  <Button
                    as={Link}
                    to="/polyglot/qc/findings?status=reported"
                    basic
                    color="blue"
                    fluid
                    size="small"
                  >
                    <Icon name="send" />
                    View Upstream Reports ({totals.reported.toLocaleString()})
                  </Button>
                  <Button
                    as={Link}
                    to="/polyglot/qc/findings?status=triaged"
                    basic
                    color="orange"
                    fluid
                    size="small"
                  >
                    <Icon name="clock" />
                    View Pending Findings ({totals.triaged.toLocaleString()})
                  </Button>
                </div>
              </Segment>
            </Grid.Column>
          </Grid>
        </>
      )}

      {/* Recent Disposition Activity (auditor timeline) */}
      {recent_activity && recent_activity.length > 0 && (
        <>
          <Header as="h2">Recent Activity</Header>
          <Table compact celled size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>When</Table.HeaderCell>
                <Table.HeaderCell>Who</Table.HeaderCell>
                <Table.HeaderCell>Action</Table.HeaderCell>
                <Table.HeaderCell>Lang</Table.HeaderCell>
                <Table.HeaderCell>Platform</Table.HeaderCell>
                <Table.HeaderCell>Error Type</Table.HeaderCell>
                <Table.HeaderCell>Msg</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {recent_activity.map((row) => {
                const actionLabels = {
                  acceptable: { text: 'Accepted', color: 'green', icon: 'check' },
                  confirmed: { text: 'Confirmed issue', color: 'orange', icon: 'exclamation triangle' },
                  false_positive: { text: 'False positive', color: 'grey', icon: 'ban' },
                };
                const action = actionLabels[row.disposition] || { text: row.disposition, color: null, icon: 'question' };
                const isFixed = row.status === 'fixed_local';

                return (
                  <Table.Row key={row.id}>
                    <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                      {row.disposition_at
                        ? new Date(row.disposition_at).toLocaleString()
                        : '\u2014'}
                    </Table.Cell>
                    <Table.Cell>
                      <strong>{row.disposition_by || '\u2014'}</strong>
                    </Table.Cell>
                    <Table.Cell>
                      <Label size="mini" color={action.color || undefined}>
                        <Icon name={action.icon} />
                        {action.text}
                      </Label>
                      {isFixed && (
                        <Label size="mini" color="green" style={{ marginLeft: '0.3em' }}>
                          <Icon name="wrench" /> Fixed
                        </Label>
                      )}
                      {row.correction_id && (
                        <span style={{ fontSize: '0.8em', color: '#888', marginLeft: '0.3em' }}>
                          (correction #{row.correction_id})
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell><strong>{row.language}</strong></Table.Cell>
                    <Table.Cell>{row.platform}</Table.Cell>
                    <Table.Cell>{row.error_type_label || row.error_type}</Table.Cell>
                    <Table.Cell>
                      <Link to={`/polyglot/messages/${row.message_id}`}>
                        {row.message_id}
                      </Link>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </>
      )}

      <Segment basic textAlign="center" style={{ marginTop: '2em' }}>
        <Link to="/polyglot/qc/findings">
          <Label size="large" color="blue">
            Browse All Findings &rarr;
          </Label>
        </Link>
        {' '}
        <Link to="/polyglot/qc/upstream">
          <Label size="large" color="orange">
            Upstream Broken Placeholders &rarr;
          </Label>
        </Link>
      </Segment>
    </Container>
  );
};

export default QCDashboard;
